import {
    Select as ShadcnSelect,
    SelectContent,
    SelectGroup,
    SelectTrigger,
    SelectValue as ShadcnSelectValue,
} from "@/components/ui/select";
import { Avatar } from "@/domains/shared/components/base/avatar/avatar";
import { HintText } from "@/domains/shared/components/base/input/hint-text";
import { Label } from "@/domains/shared/components/base/input/label";
import { requiredFieldRingWhen } from "@/domains/shared/services/formFieldStyles";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";
import type { FC, ReactNode } from "react";
import { Fragment, isValidElement, useState } from "react";
import { ComboBox } from "./combobox";
import { SelectItem } from "./select-item";
import {
    type CommonProps,
    SelectContext,
    type SelectItemType,
    type SelectKey,
    sizes,
} from "./select-shared";

export {
    SelectContext,
    sizes,
    type CommonProps,
    type SelectItemType,
    type SelectKey,
} from "./select-shared";

export interface SelectProps extends CommonProps {
    items?: SelectItemType[];
    popoverClassName?: string;
    icon?: FC | ReactNode;
    children: ReactNode | ((item: SelectItemType) => ReactNode);
    value?: SelectKey | null;
    defaultValue?: SelectKey | null;
    onValueChange?: (key: SelectKey | null) => void;
    disabled?: boolean;
    required?: boolean;
    invalid?: boolean;
    name?: string;
    className?: string;
    "aria-label"?: string;
    "aria-labelledby"?: string;
    "data-testid"?: string;
    id?: string;
}

interface SelectValueProps {
    size: "sm" | "md" | "lg";
    placeholder?: string;
    icon?: FC | ReactNode;
    selectedItem?: SelectItemType;
}

const SelectValue = ({
    size,
    placeholder,
    icon,
    selectedItem,
}: SelectValueProps) => {
    const Icon = selectedItem?.icon || icon;

    return (
        <ShadcnSelectValue
            className={cx(
                "flex min-h-0 w-full flex-1 items-center justify-start truncate text-left",
                sizes[size].root,
                (selectedItem?.icon || icon) && sizes[size].withIcon,
                "*:data-icon:shrink-0 *:data-icon:text-muted-foreground",
            )}
        >
            {selectedItem?.avatarUrl ? (
                <Avatar
                    size="xs"
                    src={selectedItem.avatarUrl}
                    alt={selectedItem.label}
                    className={cx(size === "sm" && "size-5")}
                />
            ) : isReactComponent(Icon) ? (
                <Icon data-icon aria-hidden="true" />
            ) : isValidElement(Icon) ? (
                Icon
            ) : null}

            {selectedItem ? (
                <span className={cx("flex w-full truncate", sizes[size].textContainer)}>
                    <span className={cx("truncate font-medium text-primary", sizes[size].text)}>
                        {selectedItem.label}
                    </span>
                    {selectedItem.supportingText ? (
                        <span className={cx("text-muted-foreground", sizes[size].text)}>
                            {selectedItem.supportingText}
                        </span>
                    ) : null}
                </span>
            ) : (
                <span className={cx("text-muted-foreground", sizes[size].text)}>{placeholder}</span>
            )}
        </ShadcnSelectValue>
    );
};

const Select = ({
    placeholder = "Selecionar",
    icon,
    size = "md",
    children,
    items = [],
    label,
    hint,
    tooltip,
    hideRequiredIndicator,
    className,
    popoverClassName,
    value,
    defaultValue,
    onValueChange,
    disabled,
    required,
    invalid,
    name,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "data-testid": dataTestId,
    id,
}: SelectProps) => {
    const [uncontrolledKey, setUncontrolledKey] = useState<SelectKey | null>(
        defaultValue ?? null,
    );
    const currentKey = value !== undefined ? value : uncontrolledKey;
    const selectedItem = items.find((item) => item.id === currentKey);
    const renderedItems =
        typeof children === "function"
            ? items.map((item) => (
                <Fragment key={item.id}>{children(item)}</Fragment>
            ))
            : children;

    return (
        <SelectContext.Provider value={{ size, mode: "single" }}>
            <div className={cx("flex flex-col gap-1.5", className)} data-testid={dataTestId}>
                {label ? (
                    <Label isRequired={hideRequiredIndicator ? false : required} tooltip={tooltip}>
                        {label}
                    </Label>
                ) : null}

                <ShadcnSelect
                    // Nested Dialog (e.g. DateTimePicker) already owns modal dismiss/focus;
                    // keep Select non-modal so its InternalBackdrop does not fight the parent.
                    modal={false}
                    items={items.map((item) => ({
                        label: item.label ?? String(item.id),
                        value: item.id,
                        disabled: item.isDisabled,
                    }))}
                    value={currentKey}
                    disabled={disabled}
                    required={required}
                    name={name}
                    onValueChange={(key) => {
                        const nextKey = key ?? null;
                        setUncontrolledKey(nextKey);
                        onValueChange?.(nextKey);
                    }}
                >
                    <SelectTrigger
                        id={id}
                        aria-label={ariaLabel ?? label}
                        aria-labelledby={ariaLabelledBy}
                        aria-invalid={invalid || undefined}
                        className={cx(
                            sizes[size].height,
                            "w-full cursor-pointer items-center border-0 bg-background px-0 py-0 shadow-xs ring-1 ring-inset transition duration-100 ease-linear focus-visible:ring-2 focus-visible:ring-ring",
                            requiredFieldRingWhen(required, currentKey == null || currentKey === ""),
                            sizes[size].shortcut,
                        )}
                    >
                        <SelectValue
                            size={size}
                            placeholder={placeholder}
                            icon={icon}
                            selectedItem={selectedItem}
                        />
                    </SelectTrigger>
                    <SelectContent
                        align="start"
                        alignItemWithTrigger={false}
                        className={cx(
                            size === "sm" && "max-h-56",
                            size === "md" && "max-h-64",
                            size === "lg" && "max-h-80",
                            popoverClassName,
                        )}
                    >
                        <SelectGroup>{renderedItems}</SelectGroup>
                    </SelectContent>
                </ShadcnSelect>

                {hint ? (
                    <HintText isInvalid={invalid} className={cx(size === "sm" && "text-xs")}>
                        {hint}
                    </HintText>
                ) : null}
            </div>
        </SelectContext.Provider>
    );
};

const _Select = Select as typeof Select & {
    ComboBox: typeof ComboBox;
    Item: typeof SelectItem;
};
_Select.ComboBox = ComboBox;
_Select.Item = SelectItem;

export { _Select as Select };

